/* eslint-disable consistent-return */
/* eslint-disable no-extend-native */
import React, { useState, useEffect } from 'react';

import { HashRouter as Router } from 'react-router-dom';

import { useIdleTimer } from 'react-idle-timer';

import { ConfigProvider } from 'antd';
import ptBR from 'antd/lib/locale/pt_BR';

import moment from 'moment';

import Routes from './routes';

import { SelecaoGrupoContrato } from './pages/Autorizacao/SelecaoGrupoContrato';
import { SelecaoContrato } from './pages/Autorizacao/SelecaoContrato';
import { AutenticacaoSuporte } from './pages/Autorizacao/AutenticacaoSuporte';

import { useGlobal } from './contexts/Global/context';

import { isLogged, getDecodedToken, logout } from './utils';

import { getGruposContratoAndContratosByEmail } from './services';

import AccessibilityBar from './components/AccessibilityBar';
import ContentHeader from './components/ContentHeader';
import Menu from './components/Menu';
import Button from './components/Button';
import Modal from './components/Modal';
import ScrollToTop from './components/ScrollToTop';
import Breadcrumb from './components/Breadcrumb';
import PageLoader from './components/PageLoader';
import Footer from './components/Footer';

import 'antd/dist/antd.css';
import 'material-icons/iconfont/material-icons.css';
import './styles/App.css';

import * as S from './styles/styles';
import { checkTfpSupport } from './utils/auth';

moment.updateLocale('pt_BR', {
    weekdaysMin: ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb'],
    monthsShort: ['Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun', 'Jul', 'Ago', 'Set', 'Out', 'Nov', 'Dez'],
    weekdays: ['Domingo', 'Segunda-feira', 'Terça-feira', 'Quarta-feira', 'Quinta-feira', 'Sexta-feira', 'Sábado'],
    months: [
        'Janeiro',
        'Fevereiro',
        'Março',
        'Abril',
        'Maio',
        'Junho',
        'Julho',
        'Agosto',
        'Setembro',
        'Outubro',
        'Novembro',
        'Dezembro',
    ],
});

const initialAuthorizationState = {
    grupoContratoSelecionado: false,
    contratoSelecionado: false,
    autenticado: false,
};

function App() {
    const {
        dataLogin,
        setDataLogin,
        setContractsGroupsList,
        contractGroupSelected,
        setContractGroupSelected,
        showSupportModal,
        setShowSupportModal,
    } = useGlobal();

    const [mobileOpen, setMobileOpen] = useState(false);
    const [showIdleModal, setShowIdleModal] = useState(false);

    const [authorizationState, setAuthorizationState] = useState(initialAuthorizationState);

    const [suportFlow, setSuportFlow] = useState({ step: 0 });
    const [suportDocument, setSuportDocument] = useState({ field: '', value: '' });

    const [loadingGroupContracts, setLoadingGroupContracts] = useState(true);

    const IDLEMINUTES = 30;

    function clearAuthorizationState() {
        setAuthorizationState(initialAuthorizationState);
    }

    function handleDrawerToggle() {
        if (window.innerWidth <= 600) setMobileOpen(!mobileOpen);
    }

    useIdleTimer({
        crossTab: true,
        timeout: 1000 * 60 * IDLEMINUTES,
        onIdle: () => setShowIdleModal(true),
    });

    useEffect(() => {
        if (showIdleModal) localStorage.removeItem('token');
    }, [showIdleModal]);

    useEffect(() => {
        (async () => {
            isLogged();

            const decodedToken = getDecodedToken();
            const initialDataLogin = { loggedFlow: decodedToken.tfp, email: decodedToken.email };

            setDataLogin(initialDataLogin);

            setSuportDocument({ field: 'e-mail', value: initialDataLogin.email });

            if (checkTfpSupport(decodedToken.tfp)) {
                setSuportFlow({ step: 1 });

                setLoadingGroupContracts(false);

                return;
            }

            if (initialDataLogin.email) {
                const response = await getGruposContratoAndContratosByEmail(initialDataLogin.email);

                setContractsGroupsList(response);
            }

            setLoadingGroupContracts(false);
        })();
    }, []);

    return (
        <ConfigProvider locale={ptBR}>
            <S.Main>
                <Router basename={`/${process.env.REACT_APP_ROUTE_BASENAME}`}>
                    <PageLoader loading={loadingGroupContracts} />

                    {isLogged() && suportFlow.step === 1 && (
                        <AutenticacaoSuporte
                            setLoadingGroupContracts={setLoadingGroupContracts}
                            setSuportDocument={setSuportDocument}
                            setSuportFlow={setSuportFlow}
                            setAuthorizationState={setAuthorizationState}
                        />
                    )}

                    <S.Container>
                        {isLogged() && !authorizationState.grupoContratoSelecionado && suportFlow.step !== 1 && (
                            <SelecaoGrupoContrato
                                loadingContracts={loadingGroupContracts}
                                setAuthorizationState={setAuthorizationState}
                                setContractGroupSelected={setContractGroupSelected}
                                setSuportFlow={setSuportFlow}
                                suportDocument={suportDocument}
                            />
                        )}

                        {isLogged() && authorizationState.contratoSelecionado && suportFlow.step === 2 && (
                            <SelecaoContrato
                                contractGroupSelected={contractGroupSelected}
                                setAuthorizationState={setAuthorizationState}
                                clearAuthorizationState={clearAuthorizationState}
                            />
                        )}

                        {isLogged() && authorizationState.autenticado && dataLogin.permissoes && (
                            <ScrollToTop>
                                <Menu mobileOpen={mobileOpen} handleDrawerToggle={handleDrawerToggle} />

                                <S.Content id="page-content">
                                    <AccessibilityBar />

                                    <ContentHeader handleDrawerToggle={handleDrawerToggle} />

                                    <Breadcrumb />

                                    <S.RoutesContent>
                                        <Routes />
                                    </S.RoutesContent>

                                    <Footer />
                                </S.Content>
                            </ScrollToTop>
                        )}

                        <Modal
                            title="Atenção"
                            subtitle={`Você passou mais de ${IDLEMINUTES} minutos inativo, por favor faça login novamente.`}
                            visible={showIdleModal}
                            onCancel={() => logout()}
                            footer={null}
                            style={{ maxWidth: 500 }}
                        >
                            <Button type="primary" onClick={() => logout()}>
                                Ok
                            </Button>
                        </Modal>

                        <Modal
                            title="Atenção"
                            visible={showSupportModal}
                            onCancel={() => setShowSupportModal(false)}
                            footer={null}
                            style={{ maxWidth: 500 }}
                        >
                            <p>
                                Prezado Colaborador(a),
                                <br />
                                <br />
                                Esse acesso é exclusivo para suporte e orientação ao cliente ou parceiro, sendo um
                                perfil apenas para consultas de dados.
                                <br />
                                <br />
                                Por favor, oriente o cliente ou parceiro a acessar o Portal com seus dados previamente
                                cadastrados para poder realizar as transações de dados (inclusão, alteração, exclusão e
                                etc.).
                                <br />
                                <br />
                                Agradecemos a compreensão!
                                <br />
                                <br />
                                Atenciosamente,
                                <br />
                                <br />
                                <br />
                                NotreDame Intermédica
                            </p>
                            <Button type="primary" onClick={() => setShowSupportModal(false)}>
                                Ok
                            </Button>
                        </Modal>
                    </S.Container>
                </Router>
            </S.Main>
        </ConfigProvider>
    );
}
export default App;
